import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import type { PartnerSkin } from '../../partners/types';
import '../../partners/PartnerShell.css';
import { MORE_NAV } from '../../utils/primaryNav';
import { BROADCAST_CONFERENCES } from './broadcastConferences';
import BroadcastTour from './BroadcastTour';
import { teamColorRgbChannels, type OwnMapViewMode } from './ownMapTypes';
import './BroadcastShell.css';

export interface StandingRow {
  teamId: number;
  team: string;
  pct: number;
  color: string;
  trim: string;
}

export interface SearchHit {
  fips: string;
  label: string;
}

export interface BroadcastShellProps {
  children: React.ReactNode;
  drawer?: React.ReactNode;
  activeConference: string;
  onConference: (id: string) => void;
  standings: StandingRow[];
  standingsTitle: string;
  /** Conference filter only: combined population and square miles. */
  standingsTotal?: string | null;
  query: string;
  onQueryChange: (v: string) => void;
  hits: SearchHit[];
  activeHit: number;
  onActiveHit: (i: number) => void;
  onPickHit: (hit: SearchHit) => void;
  seasonLabel: string;
  seasons: { year: number; label: string }[];
  selectedSeason: number | null;
  onSeasonChange: (year: number) => void;
  seasonTicks: string[];
  /** 0..1 along season window */
  timelinePosition: number;
  knobDay: number;
  knobLabel: string;
  onTimelinePosition: (p: number) => void;
  /** Step to previous/next snapshot date (−1 / +1). */
  onStepDate?: (delta: -1 | 1) => void;
  canStepPrev?: boolean;
  canStepNext?: boolean;
  latestLabel: string;
  latestText: string;
  tickerItems: { team: string; text: string }[];
  view: OwnMapViewMode;
  onView: (mode: OwnMapViewMode) => void;
  onSelectTeam?: (teamId: number) => void;
  /** Replaces the legacy SHARE button (MapView share menu, etc.). */
  shareSlot?: React.ReactNode;
  /** Hide broadcast chrome for clean map capture / viewing. */
  chromeHidden?: boolean;
  onShowChrome?: () => void;
  /** Map + ownership ready — gates first-visit tour auto-start. */
  mapReady?: boolean;
  /** Whitelabel partner chrome (Sleepers, etc.). */
  partner?: PartnerSkin | null;
  /** Partner CTA click (e.g. open join panel). Falls back to partner.cta.href. */
  onPartnerCta?: () => void;
}

function Crest({ color, trim, id }: { color: string; trim: string; id: string }) {
  const gid = `cg-${id.replace(/[^a-zA-Z0-9]/g, '')}`;
  return (
    <svg viewBox="0 0 40 46" aria-hidden>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity=".45" />
          <stop offset=".45" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M20 2 L37 7 V22 C37 33 29 40 20 44 C11 40 3 33 3 22 V7Z"
        fill="#cfd3d8"
        stroke="#111"
        strokeWidth="1.2"
      />
      <path d="M20 5.5 L34 9.6 V22 C34 31 27.5 37 20 40.5 C12.5 37 6 31 6 22 V9.6Z" fill={color} />
      <path d="M20 5.5 V40.5 C27.5 37 34 31 34 22 V9.6Z" fill={trim} opacity=".85" />
      <path
        d="M20 2 L37 7 V22 C37 33 29 40 20 44 C11 40 3 33 3 22 V7Z"
        fill={`url(#${gid})`}
      />
    </svg>
  );
}

function formatClockEt(): { time: string; period: string } {
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  }).formatToParts(new Date());
  const g = (t: string) => parts.find((x) => x.type === t)?.value || '';
  return { time: `${g('hour')}:${g('minute')}`, period: g('dayPeriod') };
}

/** Face text for the capsule season segment: "2026-27" / mobile "26-27". */
function seasonFaceFrom(label: string, year: number | null): { full: string; short: string } {
  const m = String(label || '').match(/(\d{4})\s*[-–]\s*(\d{2,4})/);
  if (m) {
    const end = m[2].length === 4 ? m[2].slice(-2) : m[2];
    return { full: `${m[1]}-${end}`, short: `${m[1].slice(-2)}-${end}` };
  }
  if (year != null && Number.isFinite(year)) {
    const y = Number(year);
    return { full: `${y}-${String(y + 1).slice(-2)}`, short: `${String(y).slice(-2)}-${String(y + 1).slice(-2)}` };
  }
  const fallback = String(label || 'SEASON').replace(/\s*MBB\s*/i, '').trim() || 'SEASON';
  return { full: fallback, short: fallback };
}

const BroadcastShell: React.FC<BroadcastShellProps> = ({
  children,
  drawer,
  activeConference,
  onConference,
  standings,
  standingsTitle,
  standingsTotal,
  query,
  onQueryChange,
  hits,
  activeHit,
  onActiveHit,
  onPickHit,
  seasonLabel,
  seasons,
  selectedSeason,
  onSeasonChange,
  seasonTicks,
  timelinePosition,
  knobDay,
  knobLabel,
  onTimelinePosition,
  onStepDate,
  canStepPrev = false,
  canStepNext = false,
  latestLabel,
  latestText,
  tickerItems,
  view,
  onView,
  onSelectTeam,
  shareSlot,
  chromeHidden = false,
  onShowChrome,
  mapReady = false,
  partner = null,
  onPartnerCta,
}) => {
  const { isAuthenticated, user, logout } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [confOpen, setConfOpen] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [standingsOpen, setStandingsOpen] = useState(false);
  const [tourRequest, setTourRequest] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [clock, setClock] = useState(formatClockEt);
  const moreRef = useRef<HTMLDivElement>(null);
  const confRef = useRef<HTMLDivElement>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const revealStandingsForTour = useCallback(() => setStandingsOpen(true), []);
  const revealSearchForTour = useCallback(() => {
    setSearchOpen(true);
    window.setTimeout(() => searchInputRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (!chromeHidden) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onShowChrome?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [chromeHidden, onShowChrome]);

  const power = BROADCAST_CONFERENCES.filter((c) => c.power);
  const rest = BROADCAST_CONFERENCES.filter((c) => !c.power);
  const activeRest = rest.find((c) => c.id === activeConference);
  const activeConfLabel =
    BROADCAST_CONFERENCES.find((c) => c.id === activeConference)?.label || 'All D1';
  useEffect(() => {
    const id = window.setInterval(() => setClock(formatClockEt()), 10000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!moreOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [moreOpen]);

  useEffect(() => {
    if (!confOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (confRef.current && !confRef.current.contains(e.target as Node)) {
        setConfOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [confOpen]);

  useEffect(() => {
    if (!noticeOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (noticeRef.current && !noticeRef.current.contains(e.target as Node)) {
        setNoticeOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setNoticeOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [noticeOpen]);

  const setFromClientX = (clientX: number) => {
    const track = trackRef.current;
    if (!track) return;
    const r = track.getBoundingClientRect();
    const p = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    onTimelinePosition(p);
  };

  const onTrackPointerDown = (e: React.PointerEvent) => {
    const track = trackRef.current;
    if (!track) return;
    track.setPointerCapture(e.pointerId);
    setFromClientX(e.clientX);
    const move = (ev: PointerEvent) => setFromClientX(ev.clientX);
    const up = () => {
      track.releasePointerCapture(e.pointerId);
      track.removeEventListener('pointermove', move);
      track.removeEventListener('pointerup', up);
    };
    track.addEventListener('pointermove', move);
    track.addEventListener('pointerup', up);
  };

  const tickerLoop =
    tickerItems.length > 0
      ? [...tickerItems, ...tickerItems]
      : [
          { team: '', text: 'WAITING FOR THE NEXT CONQUEST…' },
          { team: '', text: 'WAITING FOR THE NEXT CONQUEST…' },
        ];

  const atticLinks = partner?.hideEmpireNav
    ? []
    : [
        ...MORE_NAV.slice(0, 6),
        ...(isAuthenticated
          ? [{ label: user?.display_name || 'Account', path: '/me' }]
          : [{ label: 'Sign in', path: '/login' }]),
      ];

  const leaderGlow = partner
    ? partner.accent.glowRgb
    : teamColorRgbChannels(standings[0]?.color);
  const seasonFace = seasonFaceFrom(seasonLabel, selectedSeason);
  const logoHref = partner ? `/p/${partner.slug}` : '/';
  const logoLabel = partner ? `${partner.name} map home` : 'Empire Maps home';
  const wordmarkText = partner
    ? [partner.name, partner.nameSecondary].filter(Boolean).join(' ')
    : 'EMPIRE MAPS';
  const partnerStyle = partner
    ? ({
        ['--leader-glow' as string]: leaderGlow,
        ['--partner-accent' as string]: partner.accent.primary,
        ['--partner-accent-hi' as string]: partner.accent.highlight,
        ['--partner-accent-deep' as string]: partner.accent.deep,
        ['--partner-glow' as string]: partner.accent.glowRgb,
      } as React.CSSProperties)
    : ({ ['--leader-glow' as string]: leaderGlow } as React.CSSProperties);

  return (
    <div
      className={`em-broadcast${drawer ? ' em-broadcast--drawer' : ''}${
        chromeHidden ? ' em-broadcast--chrome-hidden' : ''
      }${partner ? ` em-broadcast--partner em-broadcast--partner-${partner.slug}` : ''}`}
      style={partnerStyle}
    >
      <div className="em-map" data-tour="map" aria-label="Map">
        {children}
      </div>

      <div className={`em-ui${drawer ? ' em-ui--drawer' : ''}${chromeHidden ? ' em-ui--hidden' : ''}`}>
        <header className="topbar">
          <Link className="logo" to={logoHref} aria-label={logoLabel}>
            {partner ? (
              <span className="logo-mark" aria-hidden />
            ) : (
              <svg className="logo-wings" viewBox="0 0 300 40" aria-hidden>
                <defs>
                  <linearGradient id="em-wg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#fff" />
                    <stop offset=".5" stopColor="#8d939b" />
                    <stop offset=".55" stopColor="#5a6068" />
                    <stop offset="1" stopColor="#d9dde2" />
                  </linearGradient>
                </defs>
                <path
                  d="M150 4 L156 16 L170 16 L159 24 L163 37 L150 29 L137 37 L141 24 L130 16 L144 16Z"
                  fill="#e3132c"
                  stroke="#ff9aa5"
                  strokeWidth="1"
                />
                <path
                  d="M126 20 L20 14 L60 22 L10 26 L70 30 L124 30Z M174 20 L280 14 L240 22 L290 26 L230 30 L176 30Z"
                  fill="url(#em-wg)"
                  stroke="#2b2f35"
                  strokeWidth=".8"
                />
              </svg>
            )}
            <span className="wordmark" data-text={wordmarkText}>
              {partner ? (
                <>
                  <span className="wm-partner-primary">{partner.name}</span>
                  {partner.nameSecondary ? (
                    <>
                      {' '}
                      <span className="wm-partner-secondary">{partner.nameSecondary}</span>
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <span className="wm-empire">EMPIRE</span> <span className="wm-maps">MAPS</span>
                </>
              )}
            </span>
          </Link>

          <div className={`conf-compact${confOpen ? ' open' : ''}`} ref={confRef}>
            <button
              type="button"
              className="conf-trigger"
              aria-haspopup="true"
              aria-expanded={confOpen}
              aria-label={`Conference filter: ${activeConfLabel}`}
              onClick={() => {
                setConfOpen((o) => !o);
                setMoreOpen(false);
                setSearchOpen(false);
              }}
            >
              <span>{activeConfLabel}</span>
              <em aria-hidden>▾</em>
            </button>
            {confOpen && (
              <div className="conf-panel" role="dialog" aria-label="Conferences">
                <div className="conf-panel__grid">
                  {BROADCAST_CONFERENCES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      data-id={c.id}
                      aria-pressed={c.id === activeConference}
                      onClick={() => {
                        onConference(c.id);
                        setConfOpen(false);
                      }}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                {(atticLinks.length > 0 || (!partner && isAuthenticated)) && (
                  <div className="conf-panel__links">
                    {atticLinks.map((item) => (
                      <Link
                        key={item.path + item.label}
                        to={item.path}
                        onClick={() => setConfOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                    {!partner && isAuthenticated && (
                      <button
                        type="button"
                        onClick={() => {
                          logout();
                          setConfOpen(false);
                        }}
                      >
                        Sign out
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <nav className="leagues" aria-label="Conferences">
            {power.map((c) => (
              <button
                key={c.id}
                type="button"
                data-id={c.id}
                aria-pressed={c.id === activeConference}
                onClick={() => onConference(c.id)}
              >
                {c.label}
              </button>
            ))}
            <div className={`more${moreOpen ? ' open' : ''}`} ref={moreRef}>
              <button
                type="button"
                className="more-btn"
                aria-haspopup="true"
                aria-expanded={moreOpen}
                aria-pressed={!!activeRest}
                onClick={() => setMoreOpen((o) => !o)}
              >
                {activeRest ? activeRest.label : 'More'} ▾
              </button>
              <div className="more-menu" role="menu">
                {rest.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    data-id={c.id}
                    aria-pressed={c.id === activeConference}
                    onClick={() => {
                      onConference(c.id);
                      setMoreOpen(false);
                    }}
                  >
                    {c.label}
                  </button>
                ))}
                {(atticLinks.length > 0 || (!partner && isAuthenticated)) && (
                  <div className="more-menu__links">
                    {atticLinks.map((item) => (
                      <Link
                        key={item.path + item.label}
                        to={item.path}
                        onClick={() => setMoreOpen(false)}
                      >
                        {item.label}
                      </Link>
                    ))}
                    {!partner && isAuthenticated && (
                      <button
                        type="button"
                        onClick={() => {
                          logout();
                          setMoreOpen(false);
                        }}
                      >
                        Sign out
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </nav>

          <button
            type="button"
            className="btn-search-m"
            data-tour="search"
            aria-label="Search"
            onClick={() => {
              setSearchOpen((o) => !o);
              window.setTimeout(() => searchInputRef.current?.focus(), 0);
            }}
          >
            <svg viewBox="0 0 24 24" aria-hidden>
              <circle cx="10" cy="10" r="6.5" />
              <path d="M15 15l6 6" />
            </svg>
          </button>

          <div className="search-wrap" data-tour="search">
            <label className={`search${searchOpen ? ' open' : ''}`}>
              <svg viewBox="0 0 24 24" aria-hidden>
                <circle cx="10" cy="10" r="6.5" />
                <path d="M15 15l6 6" />
              </svg>
              <input
                ref={searchInputRef}
                type="search"
                placeholder="Who owns my county?"
                aria-label="Who owns my county?"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    onActiveHit(Math.min(hits.length - 1, activeHit + 1));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    onActiveHit(Math.max(0, activeHit - 1));
                  } else if (e.key === 'Enter' && hits[activeHit]) {
                    onPickHit(hits[activeHit]);
                    setSearchOpen(false);
                  }
                }}
                autoComplete="off"
              />
            </label>
            {hits.length > 0 && (
              <ul className="search-hits">
                {hits.map((h, i) => (
                  <li
                    key={h.fips}
                    className={i === activeHit ? 'act' : ''}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onPickHit(h);
                      setSearchOpen(false);
                    }}
                  >
                    {h.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {shareSlot ?? null}

          {partner?.cta ? (
            onPartnerCta || partner.cta.href === '#join' ? (
              <button type="button" className="partner-cta" onClick={() => onPartnerCta?.()}>
                {partner.cta.eyebrow ? (
                  <span className="partner-cta__eyebrow">{partner.cta.eyebrow}</span>
                ) : null}
                <span className="partner-cta__label">{partner.cta.label}</span>
              </button>
            ) : partner.cta.href.startsWith('http') ? (
              <a
                className="partner-cta"
                href={partner.cta.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {partner.cta.eyebrow ? (
                  <span className="partner-cta__eyebrow">{partner.cta.eyebrow}</span>
                ) : null}
                <span className="partner-cta__label">{partner.cta.label}</span>
              </a>
            ) : (
              <Link className="partner-cta" to={partner.cta.href}>
                {partner.cta.eyebrow ? (
                  <span className="partner-cta__eyebrow">{partner.cta.eyebrow}</span>
                ) : null}
                <span className="partner-cta__label">{partner.cta.label}</span>
              </Link>
            )
          ) : null}
        </header>

        <aside
          className={`standings${standingsOpen ? ' open' : ''}`}
          data-tour="standings"
          aria-label="Standings"
        >
          <button
            type="button"
            className="st-head"
            aria-expanded={standingsOpen}
            onClick={() => setStandingsOpen((o) => !o)}
          >
            <span className="grip" />
            <span>{standingsTitle}</span>
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          {standingsTotal ? <p className="st-total">{standingsTotal}</p> : null}
          <ol className="st-list">
            {standings.length === 0 ? (
              <li className="empty">No teams in this conference</li>
            ) : (
              standings.map((r, i) => (
                <li
                  key={r.teamId}
                  onClick={() => onSelectTeam?.(r.teamId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSelectTeam?.(r.teamId);
                  }}
                  role={onSelectTeam ? 'button' : undefined}
                  tabIndex={onSelectTeam ? 0 : undefined}
                >
                  <span className="rank">{i + 1}</span>
                  <span className="crest">
                    <Crest color={r.color} trim={r.trim} id={`st-${r.teamId}`} />
                  </span>
                  <span className="st-name" title={r.team}>
                    {r.team}
                  </span>
                  <span className="pct">{r.pct.toFixed(1)}%</span>
                </li>
              ))
            )}
          </ol>
        </aside>

        <div className="view-toggle" role="tablist" aria-label="Map view">
          <button type="button" className={view === 'flat' ? 'on' : ''} onClick={() => onView('flat')}>
            Flat
          </button>
          <button type="button" className={view === 'globe' ? 'on' : ''} onClick={() => onView('globe')}>
            Globe
          </button>
        </div>

        <div className={`map-notice${noticeOpen ? ' open' : ''}`} ref={noticeRef}>
          <button
            type="button"
            className="map-notice__btn"
            aria-expanded={noticeOpen}
            aria-controls="em-map-notice-panel"
            onClick={() => setNoticeOpen((o) => !o)}
          >
            <svg viewBox="0 0 24 24" aria-hidden>
              <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.8" />
              <path d="M12 10.5v6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="12" cy="7.5" r="1.1" fill="currentColor" />
            </svg>
            <span>Independent</span>
          </button>
          {noticeOpen && (
            <div
              id="em-map-notice-panel"
              className="map-notice__panel"
              role="dialog"
              aria-label="Independent map notice"
            >
              <p className="map-notice__eyebrow">
                {partner?.notice?.eyebrow || 'Independent map'}
              </p>
              {partner?.notice?.paragraphs?.length ? (
                partner.notice.paragraphs.map((para) => <p key={para.slice(0, 48)}>{para}</p>)
              ) : (
                <>
                  <p>
                    Empire Maps is a fan-made conquest tracker. We are{' '}
                    <strong>not affiliated with, endorsed by, or sponsored by</strong> the NCAA or
                    any college, university, or athletics department.
                  </p>
                  <p>
                    School names and marks may appear for commentary and identification only. This
                    project is <strong>not monetized</strong> — enlistment is free, and we do not
                    sell paid ranks or school-branded goods.
                  </p>
                </>
              )}
              {!partner?.disableTour && (
                <button
                  type="button"
                  className="map-notice__tour"
                  onClick={() => {
                    setNoticeOpen(false);
                    setTourRequest((n) => n + 1);
                  }}
                >
                  Take the tour
                </button>
              )}
              {partner?.homeUrl ? (
                <p>
                  <a href={partner.homeUrl} target="_blank" rel="noopener noreferrer">
                    Visit {partner.name}
                    {partner.nameSecondary ? ` ${partner.nameSecondary}` : ''}
                  </a>
                </p>
              ) : null}
            </div>
          )}
        </div>

        {partner?.poweredByLabel ? (
          <Link className="partner-powered" to="/" title="Empire Maps">
            <span className="partner-powered__mark">EM</span>
            <span>{partner.poweredByLabel}</span>
          </Link>
        ) : null}

        <section className="timeline" data-tour="timeline" aria-label="Season timeline">
          <div className={`tl-season${seasons.length > 1 ? ' tl-season--picker' : ''}`}>
            {seasons.length > 1 ? (
              <label className="tl-season-control">
                <select
                  className="tl-season-select"
                  value={selectedSeason ?? ''}
                  aria-label="Season"
                  onChange={(e) => onSeasonChange(Number(e.target.value))}
                >
                  {seasons.map((s) => (
                    <option key={s.year} value={s.year}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <span className="tl-season-meta" aria-hidden>
                  <span className="tl-season-kicker">Season</span>
                  <span className="tl-season-face">
                    <span className="tl-season-year tl-season-year--full">{seasonFace.full}</span>
                    <span className="tl-season-year tl-season-year--short">{seasonFace.short}</span>
                    <span className="tl-season-chevron">▾</span>
                  </span>
                </span>
              </label>
            ) : (
              <span className="tl-season-meta">
                <span className="tl-season-kicker">Season</span>
                <span className="tl-season-face">
                  <span className="tl-season-year tl-season-year--full">{seasonFace.full}</span>
                  <span className="tl-season-year tl-season-year--short">{seasonFace.short}</span>
                </span>
              </span>
            )}
            <span className="tl-season-seam" aria-hidden />
          </div>
          <div className="tl-track">
            {onStepDate ? (
              <button
                type="button"
                className="tl-step tl-step--prev"
                aria-label="Previous date"
                disabled={!canStepPrev}
                onClick={() => onStepDate(-1)}
              >
                <svg viewBox="0 0 12 16" width="12" height="16" aria-hidden>
                  <path d="M10 1 L2 8 L10 15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : null}
            <div
              className="tl-track-hit"
              ref={trackRef}
              onPointerDown={onTrackPointerDown}
              role="presentation"
            >
              <div className="tl-rail" />
              <div className="tl-ticks">
                {seasonTicks.map((t, i) => (
                  <span key={t} style={{ left: `${((i + 0.5) / seasonTicks.length) * 100}%` }}>
                    {t}
                  </span>
                ))}
              </div>
              <button
                type="button"
                className="knob"
                style={{ left: `${timelinePosition * 100}%` }}
                role="slider"
                tabIndex={0}
                aria-label="Season date"
                aria-valuetext={knobLabel}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    if (onStepDate) onStepDate(1);
                    else onTimelinePosition(Math.min(1, timelinePosition + 0.01));
                  }
                  if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    if (onStepDate) onStepDate(-1);
                    else onTimelinePosition(Math.max(0, timelinePosition - 0.01));
                  }
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  onTrackPointerDown(e);
                }}
              >
                <span>{knobDay}</span>
                <em>{knobLabel}</em>
              </button>
            </div>
            {onStepDate ? (
              <button
                type="button"
                className="tl-step tl-step--next"
                aria-label="Next date"
                disabled={!canStepNext}
                onClick={() => onStepDate(1)}
              >
                <svg viewBox="0 0 12 16" width="12" height="16" aria-hidden>
                  <path d="M2 1 L10 8 L2 15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : null}
          </div>
        </section>

        <footer className="conquest">
          <div className="cq-plate">
            <span className="cq-stars">★★</span>
            <span className="cq-word">CONQUEST</span>
          </div>
          <div className="cq-mid">
            <div className="cq-head">
              <b>{latestLabel}</b>
              <i>|</i>
              {latestText}
            </div>
            <div className="cq-ticker">
              <div className="cq-track">
                {tickerLoop.map((t, i) => (
                  <span key={`${i}-${t.team}-${t.text.slice(0, 20)}`}>
                    {t.team ? <b>{t.team}</b> : null}
                    {t.team ? ' ' : ''}
                    {t.text}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="cq-live">
            <div className="live-word">
              <i />
              LIVE
            </div>
            <div className="led clock">
              {clock.time}
              <small>
                {clock.period} ET
              </small>
            </div>
          </div>
        </footer>

        {drawer}
      </div>

      {chromeHidden && (
        <button
          type="button"
          className="em-show-ui"
          onClick={() => onShowChrome?.()}
        >
          Show UI
        </button>
      )}

      <BroadcastTour
        ready={mapReady}
        disabled={chromeHidden || !!partner?.disableTour}
        openRequest={partner?.disableTour ? 0 : tourRequest}
        onStandingsReveal={revealStandingsForTour}
        onSearchReveal={revealSearchForTour}
      />
    </div>
  );
};

export default BroadcastShell;
